/* eslint-disable camelcase */
import React, { useContext } from 'react';
import { useFormik } from 'formik';
import { OnlineBookingContext } from '../..';
import BottomNav from '../BottomNav';
import Form from '../../../form-elements/Form';
import Legend from '../../../form-elements/Legend';
import Select from '../../../form-elements/Select';
import Required from '../../../form-elements/Required';
import RadioGroup from '../../../form-elements/RadioGroup';
import TextArea from '../../../form-elements/TextArea';

const TravelDetails = ({ savedTravelDetails }) => {
  const {
    form: {
      page: { traveller },
    },
    initialTravelDetailsValues,
    travelDetailsValidationSchema,
    selectedDeparture,
    disable,
  } = useContext(OnlineBookingContext);

  const {
    handleBlur,
    touched,
    values,
    errors,
    setFieldValue,
    setValues,
    setTouched,
  } = useFormik({
    initialValues: savedTravelDetails || initialTravelDetailsValues,
    validationSchema: travelDetailsValidationSchema,
    enableReinitialize: true,
  });

  const {
    traveller_details: {
      title: travellerDetailsTitle,
      fields: {
        no_travellers: noTravellersMeta,
        rooms_required: {
          title: roomsRequiredTitle,
          text: roomsRequiredText,
          fields: {
            single_room: singleRoomMeta,
            double_room: doubleRoomMeta,
            single_supplement: singleSupplementMeta,
          },
        },
      },
    },
    flights_extensions: {
      fields: {
        international_flight: internationalFlightMeta,
        international_details: internationalFlightDetailsMeta,
        extensions: extensionsMeta,
        extensions_details: extensionsDetailsMeta,
      },
    },
  } = traveller;

  const {
    no_travellers,
    single_room,
    double_room,
    share,
    quote_international,
    international_details,
    extensions,
    extensions_details,
    honeypot,
  } = values;

  return (
    <section className="c-form c-form--travel-details">
      <div className="c-form__main">
        <Form className="c-form__body">
          <fieldset className="c-form__online-fieldset c-form__online-fieldset--traveller-details">
            <Legend>{travellerDetailsTitle}</Legend>

            <label className="u-a11y">
              Honeypot
              <input
                name="honeypot"
                type="hidden"
                onChange={() => setFieldValue('honeypot', true)}
                value={honeypot}
              />
            </label>

            <h1
              className="c-form__online-fieldset__title u-color--purple"
              dangerouslySetInnerHTML={{ __html: travellerDetailsTitle }}
            ></h1>

            <div className="c-form__item c-form__item--online-booking-no-travellers">
              <Select
                options={noTravellersMeta.options.filter(
                  ({ disabled }) => !disabled
                )}
                value={no_travellers}
                id={noTravellersMeta.id}
                label={
                  <>
                    {noTravellersMeta.label}
                    {noTravellersMeta.required && <Required />}
                  </>
                }
                placeholderMessage={noTravellersMeta.placeholder_option}
                handleChange={(value) => setFieldValue('no_travellers', value)}
              />
            </div>

            <h3 className="o-text">
              <strong
                dangerouslySetInnerHTML={{ __html: roomsRequiredTitle }}
              ></strong>
            </h3>

            <div
              className="c-form__text"
              dangerouslySetInnerHTML={{ __html: roomsRequiredText }}
            ></div>

            <div className="c-form__item c-form__item--online-booking-single-room">
              <Select
                options={singleRoomMeta.options.filter(
                  ({ disabled }) => !disabled
                )}
                value={single_room}
                id={singleRoomMeta.id}
                label={
                  <>
                    {singleRoomMeta.label}
                    {singleRoomMeta.required && <Required />}
                  </>
                }
                placeholderMessage={singleRoomMeta.placeholder_option}
                handleChange={(value) => setFieldValue('single_room', value)}
              />
            </div>

            <div className="c-form__item c-form__item--online-booking-double-room">
              <Select
                options={doubleRoomMeta.options.filter(
                  ({ disabled }) => !disabled
                )}
                value={double_room}
                id={doubleRoomMeta.id}
                label={
                  <>
                    {doubleRoomMeta.label}
                    {doubleRoomMeta.required && <Required />}
                  </>
                }
                placeholderMessage={doubleRoomMeta.placeholder_option}
                handleChange={(value) => setFieldValue('double_room', value)}
              />
            </div>

            <div className="c-form__item c-form__item--supplement u-mb-0">
              <div>
                <p
                  className="o-text e-h3 u-mb-3"
                  dangerouslySetInnerHTML={{
                    __html: singleSupplementMeta.title,
                  }}
                ></p>

                <p className="u-mb-0 u-color--purple">
                  <strong
                    dangerouslySetInnerHTML={{
                      __html: selectedDeparture.price.display,
                    }}
                  ></strong>{' '}
                  per person applies unless you are willing to share a room
                </p>
              </div>

              <div className="c-form__item--online-booking-radio">
                <RadioGroup
                  name={singleSupplementMeta.id}
                  id={singleSupplementMeta.id}
                  label={singleSupplementMeta.label}
                  options={singleSupplementMeta.options}
                  handleChange={(value) => setFieldValue('share', value)}
                  value={share}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="c-form__online-fieldset c-form__online-fieldset--inter-extensions">
            <h1 className="c-form__online-fieldset__title"></h1>

            <Legend>International Flights and Extensions</Legend>

            <div className="c-form__item c-form__item--online-booking-radio">
              <RadioGroup
                name={internationalFlightMeta.id}
                id={internationalFlightMeta.id}
                label={internationalFlightMeta.label}
                options={internationalFlightMeta.options}
                handleChange={(value) =>
                  setFieldValue('quote_international', value)
                }
                value={quote_international}
              />
            </div>

            <div className="c-form__item c-form__item--online-booking-radio">
              <RadioGroup
                name={extensionsMeta.id}
                id={extensionsMeta.id}
                label={extensionsMeta.label}
                options={extensionsMeta.options}
                handleChange={(value) => setFieldValue('extensions', value)}
                value={extensions}
              />
            </div>

            <div className="c-form__item">
              <TextArea
                label={
                  <>
                    {internationalFlightDetailsMeta.label}
                    {internationalFlightDetailsMeta.required && <Required />}
                  </>
                }
                name={internationalFlightDetailsMeta.id}
                id={internationalFlightDetailsMeta.id}
                placeholder={internationalFlightDetailsMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) =>
                  setFieldValue('international_details', value)
                }
                value={international_details}
                validationError={
                  errors.international_details && touched.international_details
                }
                validationMessage={errors.international_details}
              />
            </div>

            <div className="c-form__item">
              <TextArea
                label={
                  <>
                    {extensionsDetailsMeta.label}
                    {extensionsDetailsMeta.required && <Required />}
                  </>
                }
                name={extensionsDetailsMeta.id}
                id={extensionsDetailsMeta.id}
                placeholder={extensionsDetailsMeta.placeholder}
                onBlur={handleBlur}
                handleChange={(value) =>
                  setFieldValue('extensions_details', value)
                }
                value={extensions_details}
                validationError={
                  errors.extensions_details && touched.extensions_details
                }
                validationMessage={errors.extensions_details}
              />
            </div>
          </fieldset>
        </Form>
      </div>

      <BottomNav
        canContinue={!disable(errors, values, initialTravelDetailsValues)}
        values={values}
      />
    </section>
  );
};

export default TravelDetails;
